import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, DoCheck, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { catchError, debounceTime, distinctUntilChanged, filter, first, forkJoin, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { Availability } from '../../../../../models/availability';
import { CustomerProductPrice } from '../../../../../models/price';
import { Product, ProductDimension, ProductMultiDimension } from '../../../../../models/product';
import { AuthService } from '../../../../../services/auth/auth.service';
import { AvailabilityService } from '../../../../../services/availability/availability.service';
import { GungFlowService } from '../../../../../services/gung-flow/gung-flow.service';
import { PriceService } from '../../../../../services/price/price.service';
import { ProductListV2ConfigService } from '../../../../../services/product-list-v2-config/product-list-v2-config.service';
import { ProductListConfigService } from '../../../../../services/product-list-config/product-list-config.service';
import { ProductService } from '../../../../../services/products/product.service';
import { GridViewProductCardV2 } from '../../../../../components/product-grid-view-v2/product-grid-view-v2.component';
import { DynamicPageEditorHelperService } from '../../../services/dynamic-page-editor-helper/dynamic-page-editor-helper.service';
import { DynamicPageDcBaseComponent } from '../dynamic-page-dc-base/dynamic-page-dc-base.component';

interface CarouselItem {
  id: string;
  component: any;
  data: GridViewProductCardV2;
}

interface CarouselContent {
  ids: string[];
  items: CarouselItem[];
}

@Component({
  selector: 'lib-dynamic-page-dc-product-list-carousel',
  standalone: false,
  templateUrl: './dynamic-page-dc-product-list-carousel.component.html',
  styleUrl: './dynamic-page-dc-product-list-carousel.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class DynamicPageDcProductListCarouselComponent extends DynamicPageDcBaseComponent implements OnInit, DoCheck {
  productService = inject(ProductService);
  productListV2ConfigService = inject(ProductListV2ConfigService);
  productListConfig = inject(ProductListConfigService);
  priceService = inject(PriceService);
  availabilityService = inject(AvailabilityService);
  authService = inject(AuthService);
  gungFlowService = inject(GungFlowService);
  dynamicPageEditorHelperService = inject(DynamicPageEditorHelperService);

  elementId = 'product-list-carousel';

  carouselItems: CarouselItem[] = [];
  products: Product[];
  productToAdd: string;
  resolvedProductIds: string[] = [];
  carouselAlive = true;
  includeAvailability = false;
  isSales = false;
  isAnonymous = true;
  loadingAssortment = false;

  fields = {
    useProductsIds: true,
    productsIds: [],
    assortmentId: '',
    itemsMobile: 1,
    itemsTablet: 3,
    itemsDesktop: 4,
    autoplay: false,
    autoplaySpeed: 3
  };

  private refresh$ = new Subject<void>();
  // null marks ids already requested that resolved to no product, so they are not refetched on every edit
  private itemsByProductId = new Map<string, CarouselItem | null>();
  private _doCheckAutoplay: boolean = false;
  private _doCheckAutoplaySpeed: number = 3;

  customOptions: OwlOptions = {
    lazyLoad: true,
    loop: false,
    mouseDrag: false,
    touchDrag: false,
    pullDrag: false,
    dots: false,
    navSpeed: 700,
    navText: ['<i class="fas fa-arrow-left"></i>', '<i class="fas fa-arrow-right"></i>'],
    margin: 10,
    responsive: {},
    nav: true
  };

  search = (searchTerm$: Observable<string>) => {
    return searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term =>
        this.products
          .filter(product => {
            let hasHitAllTerms = true;
            const queryTerms = term.split(' ');
            const terms = this.productListConfig.getSearchTerms(product);
            queryTerms.forEach(queryTerm => {
              const locatedTerm = terms.find(searchTerm => {
                return !!searchTerm && searchTerm.toUpperCase().indexOf(queryTerm.toUpperCase()) >= 0;
              });

              hasHitAllTerms = hasHitAllTerms && !!locatedTerm;
            });
            return hasHitAllTerms;
          })
          .slice(0, 10)
      )
    );
  };

  inputFormatter = (product: Product) => product.id;

  resultFormatter = (product: Product) => product.id + ' - ' + product.name;

  ngOnInit(): void {
    super.ngOnInit();

    this.element.fields.useProductsIds = this.element.fields.useProductsIds ?? true;
    this.element.fields.productsIds = this.element.fields.productsIds ?? [];
    this.element.fields.assortmentId = this.element.fields.assortmentId ?? '';
    this.element.fields.itemsMobile = this.element.fields.itemsMobile ?? 1;
    this.element.fields.itemsTablet = this.element.fields.itemsTablet ?? 3;
    this.element.fields.itemsDesktop = this.element.fields.itemsDesktop ?? 4;
    this.element.fields.autoplay = this.element.fields.autoplay ?? false;
    this.element.fields.autoplaySpeed = this.element.fields.autoplaySpeed ?? 3;
    this._doCheckAutoplay = !!this.element.fields.autoplay;
    this._doCheckAutoplaySpeed = this.element.fields.autoplaySpeed;
    this.updateCarouselOptions();

    if (this.mode === 'editor') {
      this.loadSearchableProducts();
    }

    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isSales =
          roles.filter(role => role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SALES').length > 0;
        this.isAnonymous = roles.filter(role => role.toUpperCase() === 'ANONYMOUS').length > 0;
      });

    this.gungFlowService.getSelectedFlow().pipe(first()).subscribe(
      flow => { this.includeAvailability = flow.useAvailabilities || flow.requireAvailability; }
    );

    if (this.mode !== 'renderer') {
      return;
    }

    this.refresh$
      .pipe(
        switchMap(() => this.fetchCarouselItems()),
        takeUntil(this.unsubscribe)
      )
      .subscribe(content => this.applyCarouselContent(content));

    if (this.edit) {
      this.dynamicPageEditorHelperService.productIdsChanged
        .pipe(
          filter(changedElement => changedElement === this.element),
          takeUntil(this.unsubscribe)
        )
        .subscribe(() => this.refreshCarousel());
    }

    this.refreshCarousel();
  }

  private loadSearchableProducts() {
    this.productListConfig.getItems().pipe(
      map(products => {
        return products.flatMap(p => {
          if (p.productType === "multiDimension") {
            const mdp = p as ProductMultiDimension;
            const primaryDimensions = mdp.primaryDimension?.length
              ? mdp.primaryDimension
              : [{ id: '', name: '' } as ProductDimension];
            return primaryDimensions.flatMap(pd =>
              mdp.secondaryDimension.map(sd => ({
                ...p,
                id: mdp.modelId + pd.id + sd.id,
                name: [p.name, pd.name, sd.name].filter(part => !!part).join(' - '),
                primaryDimension: pd,
                secondaryDimension: sd
              }))
            );
          }
          return [p];
        });
      }),
      takeUntil(this.unsubscribe)
    ).subscribe(ps => this.products = ps);
  }

  addProduct(product: Product) {
    this.element.fields.productsIds = [...this.element.fields.productsIds, product.id];
    this.notifyProductIdsChanged();
    setTimeout(() => this.productToAdd = '');
  }

  deleteProduct(productId: string) {
    this.element.fields.productsIds = this.element.fields.productsIds.filter(id => id !== productId);
    this.notifyProductIdsChanged();
  }

  dropProduct(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.element.fields.productsIds, event.previousIndex, event.currentIndex);
    this.notifyProductIdsChanged();
  }

  onSourceChange() {
    this.notifyProductIdsChanged();
  }

  onAssortmentChange() {
    this.notifyProductIdsChanged();
  }

  onItemsChange() {
    this.updateCarouselOptions();
  }

  private notifyProductIdsChanged() {
    this.dynamicPageEditorHelperService.productIdsChanged.next(this.element);
  }

  private refreshCarousel() {
    this.loadingAssortment = !this.element.fields.useProductsIds && !!this.element.fields.assortmentId;
    this.refresh$.next();
  }

  private fetchCarouselItems(): Observable<CarouselContent | null> {
    return this.getResolvedProductIds().pipe(
      first(),
      switchMap(ids => {
        const missingIds = (ids ?? []).filter(id => !this.itemsByProductId.has(id));
        const missingItems$ = missingIds.length > 0 ? this.loadCarouselData(missingIds) : of([]);
        return missingItems$.pipe(
          map(missingItems => {
            const loadedById = new Map(missingItems.map(item => [item.id, item]));
            missingIds.forEach(id => this.itemsByProductId.set(id, loadedById.get(id) ?? null));
            return {
              ids: ids ?? [],
              items: (ids ?? []).map(id => this.itemsByProductId.get(id)).filter(item => !!item)
            };
          })
        );
      }),
      catchError(error => {
        console.error('Failed to load product list carousel', error);
        return of(null);
      })
    );
  }

  private applyCarouselContent(content: CarouselContent | null) {
    this.loadingAssortment = false;
    if (!content) {
      return;
    }
    this.resolvedProductIds = content.ids;
    this.carouselItems = content.items;
  }

  private getResolvedProductIds(): Observable<string[]> {
    if (this.element.fields.useProductsIds) {
      return of([...(this.element.fields.productsIds ?? [])]);
    }
    if (!this.element.fields.assortmentId) {
      return of([]);
    }
    return this.getAssortmentProducts(this.element.fields.assortmentId).pipe(
      first(),
      map(products => products.map(product => product.id))
    );
  }

  private getAssortmentProducts(assortmentId: string): Observable<Product[]> {
    if (this.productListConfig.displayProductsOnVariantLevel === true) {
      return this.productService.getProductsByAssortmentExpanded(assortmentId);
    }
    return this.productService.getProductsByAssortment(assortmentId);
  }

  private loadCarouselData(productIds: string[]): Observable<CarouselItem[]> {
    return this.productService.getProductsByIds(productIds).pipe(
      first(),
      switchMap(products => forkJoin({
        products: of(products),
        prices: this.priceService.getCurrentCustomerPrices(productIds).pipe(first()),
        availabilities: this.includeAvailability ? this.availabilityService.getAvailabilities(productIds).pipe(first()) : of([])
      })),
      map(({ products, prices, availabilities }) => {
        const productById = new Map(products.map(product => [product.id, product]));
        return productIds
          .map(id => {
            const product = productById.get(id);
            if (!product) return null;
            const price = prices.find(p => p && p.productId === id);
            const availability = (availabilities as Availability[]).find(a => a && a.productId === id);
            const itemData: GridViewProductCardV2 = this.mapItem(product, price, availability);
            return {
              id,
              component: this.productListV2ConfigService.getProductGridCardComponent(itemData),
              data: itemData
            };
          })
          .filter(item => !!item);
      })
    );
  }

  ngDoCheck(): void {
    const autoplay = !!this.element?.fields?.autoplay;
    const speed = this.element?.fields?.autoplaySpeed || 3;
    if (autoplay === this._doCheckAutoplay && speed === this._doCheckAutoplaySpeed) {
      return;
    }
    this._doCheckAutoplay = autoplay;
    this._doCheckAutoplaySpeed = speed;
    this.updateCarouselOptions();
  }

  private updateCarouselOptions() {
    this.carouselAlive = false;
    const autoplay = !!this.element.fields.autoplay;
    this.customOptions = {
      ...this.customOptions,
      loop: autoplay,
      autoplay,
      autoplayTimeout: (this.element.fields.autoplaySpeed || 3) * 1000,
      autoplayHoverPause: true,
      responsive: {
        0: {
          items: Number(this.element.fields.itemsMobile) || 1
        },
        740: {
          items: Number(this.element.fields.itemsTablet) || 3
        },
        940: {
          items: Number(this.element.fields.itemsDesktop) || 4
        }
      }
    };

    setTimeout(() => {
      this.carouselAlive = true;
    }, 100);
  }

  private mapItem(
    product: Product,
    price: CustomerProductPrice,
    availability: Availability
  ): GridViewProductCardV2 {
    return {
      product,
      price,
      availability,
      isSales: this.isSales,
      isAnonymous: this.isAnonymous,
      includeAvailability: this.includeAvailability
    };
  }
}
